<script setup lang="ts">
import type { Token } from '@/stores/game'
import { ElMessage } from 'element-plus'
import { jwtDecode } from 'jwt-decode'
import { callBacks, closeLoginDialog, getLoginGaContext, logInGameKey, showHowMode } from '@/components/layout/LoginDialog/LoginDialog'
import { readClipboardText } from '@/components/layout/LoginDialog/readClipboardText'

interface Props {
  buyMode?: boolean
}
const props = defineProps<Props>()
const { gaLoginMethods, gaLogin } = gaUtils
const isSupportClipboard = ref(false)
const loading = {
  ref: ref('')
}
const { buyOffer, checkBuyOfferWithApi } = gamesUtils
const { t } = useI18n()
const gameSettings = computed(() => {
  return gamesUtils.getGameSettings(buyOffer.value?.gameKey || gamesUtils.routeGame.value?.gameKey || logInGameKey.value)
})
function onSubmit(data: FormTypes.SubmitForm) {
  if (gameSettings.value?.gameKey) {
    useGameStore().logOut(gameSettings.value.gameKey)
    loading.ref.value = 'logInDialog'
    const gameKey = gameSettings.value.gameKey
    gamesUtils
      .apiPost(gameKey, 'auth/token/', { playerID: data.form.accountID, token: data.form.accountToken }, false)
      .then(async ({ data }) => {
        if (data.token) {
          let decodedJwt = null
          try {
            decodedJwt = jwtDecode(data.token)
          } catch (err) {
            console?.error?.('not decoded jwt', err) // no token in the log - console output ends up in Sentry breadcrumbs
          }

          const tokenData = {
            ...{ id: '', name: '', token: '', valid: '', __token: '' },
            ...{
              token: decodedJwt ? data.token : '',
              ...(decodedJwt || {}),
              __token: data.token
            }
          }
          const isConnectedGoatAccount = false
          if (gameSettings.value?.gameKey) {
            if (tokenData.id) {
              // isConnectedGoatAccount = await autoLoginGoatFromGameAccount(tokenData.__token)
              if (!isConnectedGoatAccount) {
                gamesUtils.setGameToken(gameSettings.value.gameKey, tokenData as Token)
              }
            } else {
              gamesUtils.setGameToken(gameSettings.value.gameKey, tokenData as Token)
            }
          }
          if (!gamesUtils.routeGame.value?.gameKey) {
            gamesUtils.triggerApi.callTrigger()
          }
          if ((decodedJwt && tokenData.id) || isConnectedGoatAccount) {
            if (!props.buyMode) {
              ElMessage({
                message: t('loginBox.loggedIn'),
                type: 'success',
                duration: 3000,
                offset: 10
              })
            }

            const gaLoginContext = getLoginGaContext()
            gaLoginContext.method =
              gaLoginContext?.method === gaLoginMethods.deepLink
                ? `${gaLoginMethods.deepLink}, ${gaLoginMethods.gameToken}${isConnectedGoatAccount ? `,${gaLoginMethods.goat}` : ''}`
                : `${gaLoginMethods.gameToken}${isConnectedGoatAccount ? `,${gaLoginMethods.goat}` : ''}`
            gaLogin(gaLoginContext)
            closeLoginDialog()
            checkBuyOfferWithApi(loading.ref)
          } else {
            gamesUtils.showError({ message: t('gameLoginBox.loginFailed'), appendTo: '#login-gs-game-form', offset: 10 })
          }
        } else {
          gamesUtils.showError({ message: t('gameLoginBox.loginFailed'), appendTo: '#login-gs-game-form', offset: 10 })
        }
      })
      .catch((error) => {
        if (error?.response?.data?.ban) {
          useGameStore().setLogBan(gameKey, String(error?.response?.data?.ban))
        }
        gamesUtils.catchNotifyError({ appendTo: '#login-gs-game-form', offset: 10 })(error)
      })
      .then(() => {
        loading.ref.value = ''
      })
  }
}
async function onPaste(ev: ClipboardEvent, form: FormTypes.SubmitForm['form'], formRef: FormTypes.SubmitForm['formRefer'] | null) {
  // event data is read synchronously - preventDefault() below must run before the browser inserts the text
  let pastedData = ev.clipboardData?.getData?.('Text') || ''
  if (!pastedData.length && isSupportClipboard.value) {
    pastedData = await readClipboardText()
  }
  if (pastedData?.length) {
    const checkVal = pastedData.split(' ')
    if (checkVal.length === 2) {
      ev.stopPropagation()
      ev.preventDefault()
      form.accountID = Number.isNaN(Number.parseInt(checkVal?.[0] ?? '')) ? checkVal[0] : Number.parseInt(checkVal?.[0] ?? '')
      form.accountToken = checkVal[1]
      // no callback passed → validate() rejects on invalid fields instead of resolving false; swallow it (GAMESTURE-STORE-6S)
      formRef?.validate?.()?.catch?.(() => {})
    }
  }
}
const formInterface = ref<FormTypes.SubmitForm | null>(null)
async function paste() {
  if (utilsPlugin.getApp()?.$route.query?.debug) {
    // eslint-disable-next-line no-alert
    alert('Paste data method')
  }
  const paste = await readClipboardText()
  if (paste.length && formInterface.value) {
    const pasteSplit = paste.split(' ')
    if (pasteSplit.length === 2) {
      formInterface.value.form.accountID = Number.isNaN(Number.parseInt(pasteSplit?.[0] ?? '')) ? pasteSplit[0] : Number.parseInt(pasteSplit?.[0] ?? '')
      formInterface.value.form.accountToken = pasteSplit[1]
      // no callback passed → validate() rejects on invalid fields instead of resolving false; swallow it (GAMESTURE-STORE-6S)
      formInterface.value.formRefer?.validate?.()?.catch?.(() => {})
    }
  }
}
onMounted(() => {
  isSupportClipboard.value = typeof navigator?.clipboard?.readText === 'function'
})
function onInitForm(iForm: FormTypes.SubmitForm) {
  formInterface.value = iForm
  callBacks.paste = paste
}
</script>

<template>
  <div class="login-form-wrapper mx-auto max-w-2xl big:max-w-(--breakpoint-screen-xl)">
    <div
      class="login-form flex flex-col items-center"
    >
      <div class="mx-auto w-full max-w-xl">
        <div class="mx-auto w-full max-w-md pt-1">
          <div class="px-2 mb-0">
            <div
              class="h-12 rounded-lg flex justify-between items-center text-white bg-gs-main-color overflow-hidden"
            >
              <div class="flex justify-between items-center text-base font-black w-full uppercase">
                <div>
                  <img
                    :alt="t('gameLoginBox.gameIconAlt')"
                    :src="gameSettings?.icon"
                    class="size-12 inline-block mr-1.5"
                  >
                  {{ gameSettings?.name }}
                </div>
              </div>
            </div>
            <div class="text-right">
              <el-button
                link
                size="large"
                class="underline underline-offset-2 text-xs hover:text-neutral-700"
                @click.stop="showHowMode = true"
              >
                {{ t('gameLoginBox.seeMore') }}
              </el-button>
            </div>
          </div>
          <GsFormComponent
            id="login-gs-game-form"
            class="w-full relative"
            size="large"
            hide-required-asterisk
            @submit="onSubmit"
            @init="onInitForm"
          >
            <template #default="{ submit, form, formRefer }">
              <GsFieldTextNumber
                v-focus
                api-field-name="accountID"
                :placeholder="t('gameLoginBox.playerIdPlaceholder')"
                :label="!!String(form?.accountID ?? '').length ? t('gameLoginBox.playerIdPlaceholder') : false"
                class="mt-1 big:mt-1.5 text-lg"
                required
                clearable
                :rules="[
                  { required: true, message: t('gameLoginBox.playerIdRequired'), trigger: 'blur-sm' },
                  { type: 'number', message: t('gameLoginBox.playerIdMustBeNumber') },
                  { type: 'number', min: 1, message: t('gameLoginBox.playerIdPositiveOnly'), trigger: 'change' }
                ]"
                @paste.stop="onPaste($event, form, formRefer || null)"
                @keyup.enter.stop.prevent="submit"
              />
              <GsFieldTextTrim
                api-field-name="accountToken"
                :placeholder="t('gameLoginBox.gameTokenPlaceholder')"
                :label="!!form?.accountToken?.length ? t('gameLoginBox.gameTokenPlaceholder') : false"
                class="mt-1 big:mt-1.5 text-lg"
                clearable
                required
                :rules="[{ required: true, message: t('gameLoginBox.gameTokenRequired'), trigger: 'blur-sm' }]"
                @paste.stop="onPaste($event, form, formRefer || null)"
                @keyup.enter.stop.prevent="submit"
              />
              <div
                v-if="isSupportClipboard"
                class="text-right -mt-5 mb-3 ml-auto mr-2"
              >
                <el-button
                  link
                  class="text-xs hover:text-neutral-700"
                  @click="paste"
                >
                  {{ t('gameLoginBox.pasteData') }}
                </el-button>
              </div>
              <GsFormFieldsGroup
                justify="center"
              >
                <el-button
                  color="#2c7c8b"
                  class="text-white rounded-lg uppercase text-lg font-black gs-loading"
                  size="large"
                  :loading="loading.ref.value === 'logInDialog'"
                  @click="submit"
                >
                  {{ t('logInBanner.logIn') }}
                </el-button>
              </GsFormFieldsGroup>
            </template>
          </GsFormComponent>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
</style>
